import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { siteConfig } from "@/config/website.config";
import { broker, office } from "@/config/broker.config";
import { FiPhone, FiMail, FiMapPin, FiSend, FiLoader, FiFacebook, FiStar } from "@/lib/icons";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Local Business Schema for SEO
const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "FinancialService",
  "name": siteConfig.appName,
  "description": siteConfig.description,
  "url": siteConfig.url,
  "telephone": broker.phone,
  "email": broker.email,
  "address": {
    "@type": "PostalAddress",
    "streetAddress": office.line1,
    "addressLocality": office.suburb,
    "addressRegion": office.state,
    "postalCode": office.postcode,
    "addressCountry": "AU"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "-27.6667",
    "longitude": "152.9167"
  },
  "areaServed": [
    { "@type": "City", "name": "Springfield" },
    { "@type": "City", "name": "Springfield Lakes" },
    { "@type": "City", "name": "Spring Mountain" },
    { "@type": "City", "name": "Brisbane" },
    { "@type": "City", "name": "Logan" },
    { "@type": "City", "name": "Ipswich" }
  ],
  "priceRange": "Free",
  "openingHours": "Mo-Fr 09:00-17:00",
  "sameAs": [broker.socials?.linkedin, broker.socials?.facebook].filter(Boolean)
};

export function Footer() {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic email validation
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "website" }),
      });

      const data = await response.json() as { message?: string };

      if (!response.ok) {
        throw new Error(data.message || "Failed to subscribe");
      }

      setIsSubscribed(true);
      setEmail("");
      toast.success(data.message || "Successfully subscribed!");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to subscribe. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const links = {
    services: [
      { label: "Home Loans", href: "/home-loans" },
      { label: "First Home Buyer", href: "/home-loans/first-home-buyer" },
      { label: "Refinancing", href: "/home-loans/refinancing" },
      { label: "Investment", href: "/home-loans/investment" },
    ],
    resources: [
      { label: "Calculators", href: "/calculators" },
      { label: "Market News", href: "/market-news" },
      { label: "Blog", href: "/blog" },
      { label: "Guides", href: "/guides" },
    ],
    company: [
      { label: "About Us", href: "/about" },
      { label: "Contact", href: "/contact" },
      { label: "Our Lenders", href: "/lenders" },
    ],
    legal: [
      { label: "Privacy Policy", href: "/privacy-policy" },
      { label: "Terms & Conditions", href: "/terms-conditions" },
      { label: "Disclaimer", href: "/disclaimer" },
      { label: "Complaints", href: "/complaints" },
    ],
  };

  return (
    <footer className="bg-primary text-primary-foreground">
      {/* Local Business Schema for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />

      {/* Newsletter CTA Bar */}
      <div className="border-b border-primary-foreground/10">
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-sm">Stay updated on rate changes</h3>
              <p className="text-xs text-primary-foreground/70 mt-0.5">
                Get RBA rate alerts and market updates delivered to your inbox.
              </p>
            </div>
            {isSubscribed ? (
              <p className="text-sm text-accent font-medium">
                ✓ Subscribed to updates
              </p>
            ) : (
              <form onSubmit={handleSubscribe} className="relative w-full sm:w-80">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-10 pr-12 bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50 focus-visible:border-accent focus-visible:ring-accent/30"
                  disabled={isLoading}
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={isLoading || !email.trim()}
                  aria-label={isLoading ? "Subscribing..." : "Subscribe to newsletter"}
                  className={`absolute right-1 top-1 h-8 w-8 p-0 bg-accent hover:bg-accent/90 text-accent-foreground transition-all duration-200 ${
                    email.trim() ? "opacity-100 scale-100" : "opacity-0 scale-75 pointer-events-none"
                  }`}
                >
                  {isLoading ? (
                    <FiLoader className="size-4 animate-spin" />
                  ) : (
                    <FiSend className="size-4" />
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="px-4 md:px-6 lg:px-8 py-10 md:py-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-12 gap-8 lg:gap-6">

            {/* Brand + Contact — spans wider on large screens */}
            <div className="col-span-2 sm:col-span-3 lg:col-span-4">
              <Link to="/" className="inline-block mb-4">
                <img
                  src="/logo/PATH_FinanceSolutions_LogoLong_Normal_REV.png"
                  alt="Path Financial Solutions"
                  className="h-9 w-auto"
                />
              </Link>
              <p className="text-sm text-primary-foreground/70 max-w-xs leading-relaxed">
                {broker.shortBio} Based in Springfield, serving clients Australia-wide.
              </p>

              {/* Contact Details */}
              <div className="mt-4 space-y-1.5">
                <a
                  href={`tel:${broker.phone.replace(/\s/g, "")}`}
                  className="flex items-center gap-2 text-sm text-primary-foreground/80 hover:text-accent transition-colors"
                >
                  <FiPhone className="size-3.5 shrink-0" />
                  {broker.phone}
                </a>
                <a
                  href={`mailto:${broker.email}`}
                  className="flex items-center gap-2 text-sm text-primary-foreground/80 hover:text-accent transition-colors"
                >
                  <FiMail className="size-3.5 shrink-0" />
                  {broker.email}
                </a>
                <a
                  href={office.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary-foreground/80 hover:text-accent transition-colors"
                >
                  <FiMapPin className="size-3.5 shrink-0" />
                  <span>{office.line1}, {office.suburb}, {office.state} {office.postcode}</span>
                </a>
              </div>

              {/* Social + Review */}
              <div className="flex items-center gap-2 mt-4">
                {broker.socials?.facebook && (
                  <a
                    href={broker.socials.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-primary-foreground/10 rounded-full hover:bg-primary-foreground/20 transition-colors"
                    aria-label="Follow us on Facebook"
                  >
                    <FiFacebook className="size-4" />
                  </a>
                )}
                {broker.googleReviewUrl && (
                  <a
                    href={broker.googleReviewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-primary-foreground/10 rounded-full hover:bg-primary-foreground/20 transition-colors"
                    aria-label="Review us on Google"
                  >
                    <FiStar className="size-4" />
                  </a>
                )}
              </div>
            </div>

            {/* Services */}
            <div className="lg:col-span-2">
              <h3 className="font-semibold text-sm mb-3 text-primary-foreground/90">Services</h3>
              <ul className="space-y-2">
                {links.services.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources */}
            <div className="lg:col-span-2">
              <h3 className="font-semibold text-sm mb-3 text-primary-foreground/90">Resources</h3>
              <ul className="space-y-2">
                {links.resources.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div className="lg:col-span-2">
              <h3 className="font-semibold text-sm mb-3 text-primary-foreground/90">Company</h3>
              <ul className="space-y-2">
                {links.company.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div className="lg:col-span-2">
              <h3 className="font-semibold text-sm mb-3 text-primary-foreground/90">Legal</h3>
              <ul className="space-y-2">
                {links.legal.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-primary-foreground/50">
            <p>
              &copy; {currentYear} {siteConfig.appName}. All rights reserved.
            </p>
            <p>
              ACL {siteConfig.business.acl} | ABN {siteConfig.business.abn}
            </p>
            <a
              href="https://buungroup.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-primary-foreground/50 hover:text-primary-foreground/80 transition-colors group"
            >
              <span>Built & designed by</span>
              <img
                src="/logo/buungroup-logo.svg"
                alt="Buun Group"
                className="h-3.5 w-auto group-hover:animate-spin"
              />
              <span>Buun Group</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
